// All audio generated procedurally via Web Audio API — no samples, no files, no copyright.

const AC = window.AudioContext || window.webkitAudioContext;

let ctx        = null;
let sfxBus     = null;
let ambientBus = null;

let _sfxOn      = true;
let _sfxVol     = 0.7;
let _ambientOn  = false;
let _ambientVol = 0.32;

let _ambientRunning = false;
let _schedTimer     = null;
let _currentSong    = 0;
let _currentPhrase  = 0;

// ─── AUDIO CONTEXT ────────────────────────────────────────────────────────────

function boot() {
  if (!ctx) {
    ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    sfxBus = ctx.createGain();
    sfxBus.gain.value = _sfxOn ? _sfxVol : 0;
    sfxBus.connect(master);
    ambientBus = ctx.createGain();
    ambientBus.gain.value = _ambientOn ? _ambientVol : 0;
    ambientBus.connect(master);
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
}

// ─── PRIMITIVES ───────────────────────────────────────────────────────────────

function midiToHz(n) { return 440 * Math.pow(2, (n - 69) / 12); }

function osc(freq, start, attack, hold, release, vol, type = 'sine', bus = sfxBus) {
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, start);
  g.gain.linearRampToValueAtTime(vol, start + attack);
  g.gain.setValueAtTime(vol, start + attack + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, start + attack + hold + release);
  o.connect(g);
  g.connect(bus);
  o.start(start);
  o.stop(start + attack + hold + release + 0.05);
}

function noiseBurst(start, duration, vol, filterHz, bus = sfxBus) {
  if (!ctx) return;
  const len = Math.ceil(ctx.sampleRate * duration);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d   = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = filterHz;
  f.Q.value = 1.8;
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, start);
  g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  src.connect(f); f.connect(g); g.connect(bus);
  src.start(start);
}

// ─── SFX ──────────────────────────────────────────────────────────────────────

export function playMove() {
  if (!_sfxOn) return;
  boot();
  const t = ctx.currentTime;
  noiseBurst(t, 0.045, 0.55, 1400);
  osc(105, t, 0.004, 0.01, 0.09, 0.32);
}

export function playCapture() {
  if (!_sfxOn) return;
  boot();
  const t = ctx.currentTime;
  noiseBurst(t,        0.09, 0.7,  480);
  noiseBurst(t + 0.03, 0.05, 0.3,  1000);
  osc(72, t, 0.004, 0.02, 0.18, 0.5);
}

export function playCheck() {
  if (!_sfxOn) return;
  boot();
  const t = ctx.currentTime;
  osc(880,  t,       0.004, 0.02, 0.22, 0.3,  'sine');
  osc(1109, t + 0.1, 0.004, 0.02, 0.28, 0.22, 'sine');
}

export function playGameOver(won) {
  if (!_sfxOn) return;
  boot();
  const t = ctx.currentTime;
  if (won) {
    [[523, 0], [659, 0.13], [784, 0.26], [1047, 0.40]].forEach(([f, dt]) =>
      osc(f, t + dt, 0.008, 0.06, 0.55, 0.2)
    );
    osc(1568, t + 0.55, 0.01, 0.1, 0.9, 0.1);
  } else {
    [[494, 0], [415, 0.22], [349, 0.46], [262, 0.72]].forEach(([f, dt]) =>
      osc(f, t + dt, 0.008, 0.06, 0.65, 0.15)
    );
  }
}

export function playBlancheOpen() {
  if (!_sfxOn) return;
  boot();
  const t = ctx.currentTime;
  [[880, 0], [1109, 0.07], [1318, 0.15], [1760, 0.23]].forEach(([f, dt]) =>
    osc(f, t + dt, 0.008, 0.04, 0.38, 0.065)
  );
}

// ─── AMBIENT INSTRUMENTS ──────────────────────────────────────────────────────

function padChord(midiNotes, t, dur, vol = 0.028) {
  if (!ctx) return;
  const atk  = Math.min(1.2, dur * 0.18);
  const rel  = Math.min(1.4, dur * 0.22);
  const hold = Math.max(0.01, dur - atk - rel);
  midiNotes.forEach((midi, i) => {
    const hz = midiToHz(midi);
    const v  = i === 0 ? vol * 1.5 : vol;
    osc(hz,                    t, atk, hold, rel, v,        'sine',     ambientBus);
    osc(midiToHz(midi + 0.05), t, atk, hold, rel, v * 0.28, 'triangle', ambientBus);
  });
}

function pianoNote(midi, t, durSec, vol = 0.032) {
  if (!ctx) return;
  const hz   = midiToHz(midi);
  const hold = Math.max(0.01, durSec * 0.35);
  const rel  = Math.max(0.05, durSec * 0.55);
  osc(hz,     t, 0.007, hold, rel,  vol,        'sine', ambientBus);
  osc(hz * 2, t, 0.005, 0.08, 0.14, vol * 0.22, 'sine', ambientBus);
}

function bassNote(midi, t, durSec, vol = 0.042) {
  if (!ctx) return;
  osc(midiToHz(midi), t, 0.018, Math.max(0.01, durSec - 0.14), 0.12, vol, 'sine', ambientBus);
}

function saxNote(midi, t, durSec, vol = 0.038) {
  if (!ctx || durSec <= 0.05) return;
  const hz  = midiToHz(midi);
  const o   = ctx.createOscillator();
  const flt = ctx.createBiquadFilter();
  const g   = ctx.createGain();
  const lfo = ctx.createOscillator();
  const lg  = ctx.createGain();
  o.type = 'sawtooth';
  o.frequency.value = hz;
  lfo.frequency.value = 5.2;
  lg.gain.setValueAtTime(0, t);
  lg.gain.linearRampToValueAtTime(hz * 0.012, t + Math.min(0.2, durSec * 0.4));
  lfo.connect(lg);
  lg.connect(o.frequency);
  flt.type = 'bandpass';
  flt.frequency.value = 1200;
  flt.Q.value = 1.8;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.055);
  g.gain.setValueAtTime(vol, Math.max(t + 0.06, t + durSec - 0.1));
  g.gain.exponentialRampToValueAtTime(0.0001, t + durSec);
  o.connect(flt); flt.connect(g); g.connect(ambientBus);
  lfo.start(t); lfo.stop(t + durSec + 0.05);
  o.start(t);   o.stop(t + durSec + 0.05);
}

function guitarNote(midi, t, durSec, vol = 0.030) {
  if (!ctx) return;
  const hz = midiToHz(midi);
  osc(hz,         t, 0.009, durSec * 0.45, durSec * 0.45, vol,        'triangle', ambientBus);
  osc(hz * 1.003, t, 0.009, durSec * 0.35, durSec * 0.45, vol * 0.38, 'triangle', ambientBus);
  osc(hz * 2,     t, 0.006, durSec * 0.15, durSec * 0.10, vol * 0.15, 'sine',     ambientBus);
}

function bell(midi, t, durSec, vol = 0.015) {
  if (!ctx) return;
  const hz = midiToHz(midi);
  osc(hz,   t, 0.005, durSec * 0.25, durSec * 0.70, vol,        'sine', ambientBus);
  osc(hz*4, t, 0.004, 0.02,          0.30,           vol * 0.12, 'sine', ambientBus);
}

function kickDrum(t) {
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.frequency.setValueAtTime(150, t);
  o.frequency.exponentialRampToValueAtTime(52, t + 0.09);
  g.gain.setValueAtTime(0.45, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
  o.connect(g); g.connect(ambientBus);
  o.start(t); o.stop(t + 0.32);
}

function snareDrum(t) {
  if (!ctx) return;
  noiseBurst(t, 0.10, 0.16, 2400, ambientBus);
  osc(200, t, 0.002, 0.01, 0.055, 0.09, 'sine', ambientBus);
}

function hihatTick(t, vol = 0.045) {
  if (!ctx) return;
  const len = Math.ceil(ctx.sampleRate * 0.028);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d   = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = 'highpass'; f.frequency.value = 7500;
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.028);
  src.connect(f); f.connect(g); g.connect(ambientBus);
  src.start(t);
}

function rimshotTick(t) {
  if (!ctx) return;
  noiseBurst(t, 0.038, 0.07, 3600, ambientBus);
}

// ─── SONGS ────────────────────────────────────────────────────────────────────

// Blue Dusk — slow jazz ballad, 72 BPM, pads + walking melody
const BLUE_DUSK = (() => {
  const BPM = 72, BEAT = 60 / BPM, BAR = BEAT * 4, PHRASE = BAR * 2;
  const CHORDS = [
    [50, 53, 57, 60],  // Dm7
    [55, 59, 62, 65],  // G7
    [48, 52, 55, 59],  // Cmaj7
    [45, 48, 52, 55],  // Am7
  ];
  const BASS   = [38, 43, 36, 45];
  const MELODY = [
    [[72, 1.5], [69, 1.0], [65, 2.0]],
    [[71, 1.5], [67, 1.0], [62, 2.0]],
    [[76, 1.5], [72, 1.0], [67, 2.0]],
    [[72, 1.5], [69, 1.0], [64, 2.0]],
  ];
  return {
    name: "Blue Dusk",
    phraseCount: 10,
    schedulePhrase(t, i) {
      const ci = i % 4;
      padChord(CHORDS[ci], t, PHRASE * 1.1);
      bassNote(BASS[ci], t, PHRASE * 0.88);
      let mt = t + BEAT * 0.5;
      MELODY[ci].forEach(([midi, beats]) => {
        pianoNote(midi, mt, beats * BEAT * 0.82, 0.018);
        mt += beats * BEAT;
      });
      return PHRASE;
    },
  };
})();

// Midnight Swing — medium swing jazz, 108 BPM, sax + drums + piano
const MIDNIGHT_SWING = (() => {
  const BPM = 108, BEAT = 60 / BPM, BAR = BEAT * 4, PHRASE = BAR * 2;
  const CHORDS = [
    [45, 48, 52, 55],  // Am7
    [38, 42, 45, 50],  // D7
    [43, 46, 50, 53],  // Gm7
    [36, 40, 43, 47],  // C7
  ];
  const BASS = [45, 38, 43, 36];
  const SAX  = [
    [[69, 1.0], [72, 0.5], [71, 0.75], [69, 0.75]],
    [[66, 1.0], [69, 0.5], [67, 0.75], [65, 0.75]],
    [[67, 1.0], [70, 0.5], [69, 0.75], [67, 0.75]],
    [[64, 1.0], [67, 0.5], [65, 0.75], [64, 0.75]],
  ];
  return {
    name: "Midnight Swing",
    phraseCount: 16,
    schedulePhrase(t, i) {
      const ci = i % 4;
      CHORDS[ci].forEach((midi, idx) => pianoNote(midi, t + idx * 0.016, BEAT * 1.4, 0.026));
      bassNote(BASS[ci], t, BEAT * 2, 0.040);
      bassNote(BASS[ci] - 2, t + BEAT * 2, BEAT * 2, 0.034);
      let mt = t + BEAT * 0.5;
      SAX[ci].forEach(([midi, beats]) => {
        saxNote(midi, mt, beats * BEAT * 0.85, 0.036);
        mt += beats * BEAT * 1.12;
      });
      for (let bar = 0; bar < 2; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          const bt = t + (bar * 4 + beat) * BEAT;
          hihatTick(bt, 0.040);
          hihatTick(bt + BEAT * 0.67, 0.022);
          if (beat === 0) kickDrum(bt);
          if (beat === 2) kickDrum(bt + BEAT * 0.5);
          if (beat === 1 || beat === 3) snareDrum(bt);
        }
      }
      return PHRASE;
    },
  };
})();

// Café Bossa — bossa nova, 116 BPM, guitar melody + rimshot
const CAFE_BOSSA = (() => {
  const BPM = 116, BEAT = 60 / BPM, BAR = BEAT * 4, PHRASE = BAR * 2;
  const CHORDS = [
    [57, 60, 64],      // Am
    [52, 56, 59, 62],  // E7
    [53, 57, 60, 64],  // Fmaj7
    [55, 59, 62, 65],  // G7
  ];
  const BASS   = [45, 40, 41, 43];
  const MELODY = [
    [[76, 0.75], [74, 0.5], [72, 1.0], [69, 0.75]],
    [[71, 0.75], [69, 0.5], [68, 1.0], [64, 0.75]],
    [[65, 0.75], [67, 0.5], [69, 1.0], [67, 0.75]],
    [[74, 0.75], [72, 0.5], [71, 1.0], [67, 0.75]],
  ];
  return {
    name: "Café Bossa",
    phraseCount: 16,
    schedulePhrase(t, i) {
      const ci = i % 4;
      padChord(CHORDS[ci], t, PHRASE * 1.05, 0.018);
      bassNote(BASS[ci], t, BEAT * 2, 0.036);
      bassNote(BASS[ci], t + BEAT * 2.5, BEAT * 1.5, 0.030);
      let mt = t + BEAT * 0.25;
      MELODY[ci].forEach(([midi, beats]) => {
        guitarNote(midi, mt, beats * BEAT * 0.88);
        mt += beats * BEAT;
      });
      for (let bar = 0; bar < 2; bar++) {
        const bt = t + bar * BAR;
        hihatTick(bt, 0.030);
        rimshotTick(bt + BEAT);
        hihatTick(bt + BEAT * 1.5, 0.020);
        hihatTick(bt + BEAT * 2, 0.030);
        rimshotTick(bt + BEAT * 3);
        hihatTick(bt + BEAT * 3.5, 0.020);
      }
      return PHRASE;
    },
  };
})();

// Rain on Keys — solo piano, 60 BPM, no drums
const RAIN_ON_KEYS = (() => {
  const BPM = 60, BEAT = 60 / BPM, BAR = BEAT * 4, PHRASE = BAR * 4;
  const CHORDS = [
    { pad: [50, 53, 57, 60, 64], bass: 38, arp: [50, 53, 57, 60, 64, 60, 57, 53] },
    { pad: [55, 59, 62, 65],     bass: 43, arp: [55, 59, 62, 65, 62, 59, 55, 59] },
    { pad: [48, 52, 55, 59],     bass: 36, arp: [48, 52, 55, 59, 55, 52, 48, 52] },
    { pad: [45, 48, 52, 55, 60], bass: 45, arp: [45, 52, 55, 60, 55, 52, 45, 48] },
  ];
  return {
    name: "Rain on Keys",
    phraseCount: 5,
    schedulePhrase(t, i) {
      const c = CHORDS[i % CHORDS.length];
      padChord(c.pad, t, PHRASE * 1.1, 0.022);
      pianoNote(c.bass, t, BEAT * 2, 0.038);
      pianoNote(c.bass, t + BEAT * 2, BEAT * 2, 0.030);
      c.arp.forEach((midi, idx) => {
        const vol = 0.022 + (idx % 4 === 0 ? 0.010 : 0);
        pianoNote(midi, t + idx * BEAT * 0.5, BEAT * 0.42, vol);
      });
      return PHRASE;
    },
  };
})();

// Ember Hours — ambient pads + bells, 50 BPM
const EMBER_HOURS = (() => {
  const BPM = 50, BEAT = 60 / BPM, BAR = BEAT * 4, PHRASE = BAR * 4;
  const PADS = [
    [48, 52, 55, 59, 64],  // Cmaj9
    [41, 45, 48, 53, 57],  // Fmaj9
    [45, 48, 52, 55, 60],  // Am9
    [43, 47, 50, 55, 59],  // Gmaj9
  ];
  const BELLS = [
    [[81, 2.5], [76, 1.5], [72, 3.0]],
    [[84, 2.5], [81, 1.5], [77, 3.0]],
    [[76, 2.5], [72, 1.5], [69, 3.0]],
    [[74, 2.5], [71, 1.5], [67, 3.0]],
  ];
  return {
    name: "Ember Hours",
    phraseCount: 4,
    schedulePhrase(t, i) {
      const ci = i % 4;
      padChord(PADS[ci], t, PHRASE * 1.15, 0.024);
      let mt = t + BEAT * 4;
      BELLS[ci].forEach(([midi, beats]) => {
        bell(midi, mt, beats * BEAT, 0.013);
        mt += beats * BEAT;
      });
      return PHRASE;
    },
  };
})();

export const SONGS     = [BLUE_DUSK, MIDNIGHT_SWING, CAFE_BOSSA, RAIN_ON_KEYS, EMBER_HOURS];
export const SONG_NAMES = SONGS.map(s => s.name);

// ─── SCHEDULER ────────────────────────────────────────────────────────────────

function scheduleNextPhrase() {
  if (!_ambientRunning || !ctx) return;
  const song = SONGS[_currentSong];
  const t    = ctx.currentTime + 0.05;
  const dur  = song.schedulePhrase(t, _currentPhrase);

  _currentPhrase++;
  if (_currentPhrase >= song.phraseCount) {
    _currentPhrase = 0;
    _currentSong   = (_currentSong + 1) % SONGS.length;
  }

  _schedTimer = setTimeout(scheduleNextPhrase, Math.max(100, (dur - 0.15) * 1000));
}

// ─── START / STOP ─────────────────────────────────────────────────────────────

export function startAmbient() {
  if (_ambientRunning) return;
  boot();
  _ambientRunning = true;
  if (ambientBus) ambientBus.gain.value = _ambientOn ? _ambientVol : 0;
  scheduleNextPhrase();
}

export function stopAmbient() {
  _ambientRunning = false;
  if (_schedTimer) { clearTimeout(_schedTimer); _schedTimer = null; }
  if (ambientBus && ctx) {
    ambientBus.gain.setValueAtTime(ambientBus.gain.value, ctx.currentTime);
    ambientBus.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 1.5);
    setTimeout(() => { if (!_ambientOn && ambientBus) ambientBus.gain.value = 0; }, 1600);
  }
}

// ─── CONFIGURE ────────────────────────────────────────────────────────────────

export function configure({ sfxOn, sfxVol, ambientOn, ambientVol, ambientSong }) {
  if (sfxOn      !== undefined) _sfxOn      = sfxOn;
  if (sfxVol     !== undefined) _sfxVol     = sfxVol;
  if (ambientOn  !== undefined) _ambientOn  = ambientOn;
  if (ambientVol !== undefined) _ambientVol = ambientVol;

  if (ambientSong !== undefined && ambientSong !== _currentSong) {
    _currentSong   = Math.max(0, Math.min(SONGS.length - 1, ambientSong));
    _currentPhrase = 0;
    if (_ambientOn && _ambientRunning) {
      stopAmbient();
      setTimeout(startAmbient, 200);
      return;
    }
  }

  if (sfxBus)     sfxBus.gain.value     = _sfxOn    ? _sfxVol    : 0;
  if (ambientBus) ambientBus.gain.value  = _ambientOn ? _ambientVol : 0;

  if (_ambientOn  && !_ambientRunning) startAmbient();
  if (!_ambientOn && _ambientRunning)  stopAmbient();
}
